import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import { N50, N800 } from '@atlaskit/theme/colors';
import TallButton from './TallButton';

import messages from './SocialLoginForm.messages';

import googleLogo from '../images/google-logo.svg';
import microsoftLogo from '../images/microsoft-logo.svg';
import appleLogo from '../images/apple-logo.svg';
import slackLogo from '../images/slack-logo.svg';

import { SocialProvider } from '../types';

export type SocialLoginFormProps = {
  email?: string;
  highlightedSocialProvider: SocialProvider;
  handleGoogleLogin: () => void;
  handleMicrosoftLogin: () => void;
  handleAppleLogin: () => void;
  handleSlackLogin: () => void;
};

const SocialLoginBlock = styled.div`
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${gridSize() * 1.5}px;

  border: 1px solid ${N50};
  border-radius: 3px;
`;

const Header = styled.div`
  display: flex;
  align-self: center;
  justify-content: center;

  width: 100%;
  padding-bottom: ${gridSize() * 1.5}px;
`;

const Email = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: ${gridSize() * 3}px;
  color: ${N800};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Logo = styled.img`
  height: ${gridSize() * 3}px;
  padding-right: ${gridSize()}px;
`;

const getLogo = (highlightedSocialProvider: SocialProvider) => {
  switch (highlightedSocialProvider) {
    case SocialProvider.Google:
      return googleLogo;
    case SocialProvider.Microsoft:
      return microsoftLogo;
    case SocialProvider.Apple:
      return appleLogo;
    case SocialProvider.Slack:
      return slackLogo;
  }
};

const getSocialLoginHandler = ({
  highlightedSocialProvider,
  handleGoogleLogin,
  handleMicrosoftLogin,
  handleAppleLogin,
  handleSlackLogin,
}: SocialLoginFormProps) => {
  switch (highlightedSocialProvider) {
    case SocialProvider.Google:
      return handleGoogleLogin;
    case SocialProvider.Microsoft:
      return handleMicrosoftLogin;
    case SocialProvider.Apple:
      return handleAppleLogin;
    case SocialProvider.Slack:
      return handleSlackLogin;
  }
};

const SocialLoginForm = (props: SocialLoginFormProps) => {
  const { highlightedSocialProvider, email } = props;
  const handleSocialLogin = getSocialLoginHandler(props);

  return (
    <SocialLoginBlock data-testid="social-login-form">
      <Header>
        <Logo src={getLogo(highlightedSocialProvider)} alt={`${highlightedSocialProvider} logo`} />
        {email && <Email>{email}</Email>}
      </Header>
      <TallButton
        isFullWidth
        id="social-login-submit"
        appearance="primary"
        onClick={handleSocialLogin}>
        <FormattedMessage
          {...messages.continueWith}
          values={{
            socialProviderName: highlightedSocialProvider.replace(/^./, str => str.toUpperCase()),
          }}
        />
      </TallButton>
    </SocialLoginBlock>
  );
};

export default SocialLoginForm;
